import React, { useState } from 'react';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 12,
  },
  header: {
    textAlign: 'center',
    marginBottom: 20,
    borderBottom: 1,
    paddingBottom: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
  },
  section: {
    marginBottom: 15,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  label: {
    width: 120,
    fontWeight: 'bold',
  },
  value: {
    flex: 1,
  },
  table: {
    marginTop: 20,
    marginBottom: 20,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f0f0f0',
    padding: 8,
    fontWeight: 'bold',
  },
  tableRow: {
    flexDirection: 'row',
    padding: 8,
    borderBottom: 0.5,
    borderBottomColor: '#ccc',
  },
  col1: { width: '40%' },
  col2: { width: '15%', textAlign: 'center' },
  col3: { width: '20%', textAlign: 'right' },
  col4: { width: '15%', textAlign: 'right' },
  col5: { width: '10%', textAlign: 'right' },
  totals: {
    marginTop: 20,
    alignSelf: 'flex-end',
    width: 200,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 5,
  },
  totalLabel: {
    fontWeight: 'bold',
  },
  grandTotal: {
    borderTop: 2,
    borderTopColor: '#000',
    paddingTop: 10,
    marginTop: 10,
  },
});

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED'
  }).format(amount || 0);
};

const InvoicePDF = ({ formData, totals }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>TAX INVOICE</Text>
        <Text style={styles.subtitle}>Federal Tax Authority - UAE</Text>
      </View>

      {/* Invoice Details */}
      <View style={styles.section}>
        <View style={styles.row}>
          <Text style={styles.label}>Invoice No:</Text>
          <Text style={styles.value}>{formData.invoiceNo}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Date:</Text>
          <Text style={styles.value}>{formData.invoiceDate}</Text>
        </View>
      </View>

      {/* Customer Info */}
      <View style={styles.section}>
        <Text style={[styles.label, { marginBottom: 10 }]}>Bill To:</Text>
        <Text>{formData.customerName}</Text>
        {formData.customerTRN && <Text>TRN: {formData.customerTRN}</Text>}
      </View>

      {/* Items Table */}
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.col1}>Description</Text>
          <Text style={styles.col2}>Qty</Text>
          <Text style={styles.col3}>Unit Price</Text>
          <Text style={styles.col4}>VAT Rate</Text>
          <Text style={styles.col5}>Total</Text>
        </View>

        {formData.items.map((item, index) => (
          <View key={index} style={styles.tableRow}>
            <Text style={styles.col1}>{item.description}</Text>
            <Text style={styles.col2}>{item.quantity}</Text>
            <Text style={styles.col3}>{formatCurrency(item.unitPrice)}</Text>
            <Text style={styles.col4}>{item.vatPercent}%</Text>
            <Text style={styles.col5}>
              {formatCurrency(item.quantity * item.unitPrice * (1 + item.vatPercent / 100))}
            </Text>
          </View>
        ))}
      </View>

      {/* Totals */}
      <View style={styles.totals}>
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Subtotal:</Text>
          <Text>{formatCurrency(totals.subtotal)}</Text>
        </View>
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>VAT Amount:</Text>
          <Text>{formatCurrency(totals.vatTotal)}</Text>
        </View>
        <View style={[styles.totalRow, styles.grandTotal]}>
          <Text style={[styles.totalLabel, { fontSize: 14 }]}>Total Amount:</Text>
          <Text style={{ fontSize: 14, fontWeight: 'bold' }}>{formatCurrency(totals.total)}</Text>
        </View>
      </View>

      {/* Footer */}
      <View style={[styles.section, { marginTop: 30, textAlign: 'center' }]}>
        <Text style={{ fontSize: 10, color: '#666' }}>
          This is a VAT compliant invoice issued in accordance with UAE Federal Tax Authority regulations.
        </Text>
        <Text style={{ fontSize: 10, color: '#666' }}>
          All amounts are in UAE Dirhams (AED). VAT Rate: 5% (standard rate).
        </Text>
      </View>
    </Page>
  </Document>
);

const UAEVATInvoice = () => {
  const [formData, setFormData] = useState({
    invoiceNo: `INV-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
    invoiceDate: new Date().toISOString().split('T')[0],
    customerName: '',
    customerTRN: '',
    items: [
      {
        description: '',
        quantity: 1,
        unitPrice: 0,
        vatPercent: 5
      }
    ]
  });

  const [loading, setLoading] = useState(false);
  const [showPDFDownload, setShowPDFDownload] = useState(false);

  // Update form field
  const updateField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Update item
  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = {
      ...newItems[index],
      [field]: field === 'quantity' || field === 'unitPrice' || field === 'vatPercent'
        ? parseFloat(value) || 0
        : value
    };
    setFormData(prev => ({
      ...prev,
      items: newItems
    }));
  };

  // Add item
  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          description: '',
          quantity: 1,
          unitPrice: 0,
          vatPercent: 5
        }
      ]
    }));
  };

  // Remove item
  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let vatTotal = 0;

    formData.items.forEach(item => {
      const itemSubtotal = item.quantity * item.unitPrice;
      const itemVat = itemSubtotal * (item.vatPercent / 100);
      subtotal += itemSubtotal;
      vatTotal += itemVat;
    });

    return {
      subtotal,
      vatTotal,
      total: subtotal + vatTotal
    };
  };

  const totals = calculateTotals();

  // Save invoice
  const handleSave = async () => {
    if (!formData.customerName) {
      alert('Please enter customer name');
      return;
    }

    if (formData.items.some(item => !item.description)) {
      alert('Please enter description for all items');
      return;
    }

    setLoading(true);

    try {
      // Simulate save to backend
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowPDFDownload(true);
      alert('Invoice saved successfully! PDF download will be available.');
    } catch (error) {
      alert('Error saving invoice');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-6">
      <h1 className="text-2xl font-bold text-center mb-6">UAE VAT Invoice</h1>

      {/* Invoice Header */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium mb-1">Invoice Number</label>
          <input
            type="text"
            value={formData.invoiceNo}
            onChange={(e) => updateField('invoiceNo', e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Invoice Date</label>
          <input
            type="date"
            value={formData.invoiceDate}
            onChange={(e) => updateField('invoiceDate', e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>
      </div>

      {/* Customer Info */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium mb-1">Customer Name *</label>
          <input
            type="text"
            value={formData.customerName}
            onChange={(e) => updateField('customerName', e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
            placeholder="Enter customer name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Customer TRN</label>
          <input
            type="text"
            value={formData.customerTRN}
            onChange={(e) => updateField('customerTRN', e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
            placeholder="15-digit TRN or N/A"
          />
        </div>
      </div>

      {/* Items */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Items</h3>
          <button
            onClick={addItem}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Add Item
          </button>
        </div>

        {formData.items.map((item, index) => (
          <div key={index} className="border rounded-md p-4 mb-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium">Item {index + 1}</h4>
              {formData.items.length > 1 && (
                <button
                  onClick={() => removeItem(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Description *</label>
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => updateItem(index, 'description', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Item description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Quantity</label>
                <input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Unit Price (AED)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.unitPrice}
                  onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">VAT Rate (%)</label>
                <select
                  value={item.vatPercent}
                  onChange={(e) => updateItem(index, 'vatPercent', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="0">0% (Zero-rated)</option>
                  <option value="5">5% (Standard)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Line Total</label>
                <div className="px-3 py-2 bg-gray-100 rounded-md font-medium">
                  {formatCurrency(item.quantity * item.unitPrice * (1 + item.vatPercent / 100))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Totals */}
      <div className="flex justify-end mb-6">
        <div className="w-64">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>{formatCurrency(totals.subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span>VAT Amount:</span>
              <span>{formatCurrency(totals.vatTotal)}</span>
            </div>
            <div className="border-t-2 border-gray-300 pt-2 flex justify-between text-xl font-bold">
              <span>Total:</span>
              <span>{formatCurrency(totals.total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="text-center">
        {!showPDFDownload ? (
          <button
            onClick={handleSave}
            disabled={loading}
            className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 disabled:opacity-50 font-medium"
          >
            {loading ? 'Saving...' : 'Save Invoice'}
          </button>
        ) : (
          <div className="space-y-4">
            <p className="text-green-600 font-medium">Invoice saved successfully!</p>
            <PDFDownloadLink
              document={<InvoicePDF formData={formData} totals={totals} />}
              fileName={`Invoice-${formData.invoiceNo}.pdf`}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 font-medium inline-block"
            >
              {({ loading: pdfLoading }) =>
                pdfLoading ? 'Generating PDF...' : 'Download PDF'
              }
            </PDFDownloadLink>
            <div>
              <button
                onClick={() => setShowPDFDownload(false)}
                className="text-gray-600 hover:text-gray-800 underline"
              >
                Create Another Invoice
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UAEVATInvoice;